import { useCallback, useEffect, useState } from 'react';
import { getPreferences, savePreferences, ThemeColor, ThemeMode, UserPreferences } from '../utils/storage';
import { t, type Language } from '../utils/i18n';
import { audioEngine } from '../utils/audio';

/**
 * Donker of licht? 'auto' volgt het systeem en schakelt tussen 20:00 en 07:00 altijd naar donker.
 */
export function isDarkModeActive(mode: ThemeMode, systemDark: boolean, now = new Date()): boolean {
  if (mode === 'dark') return true;
  if (mode === 'light') return false;
  const hour = now.getHours();
  return systemDark || hour >= 20 || hour < 7;
}

const systemPrefersDark = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-color-scheme: dark)').matches;

/** Alle bewaarde voorkeuren, plus het toepassen ervan op de pagina (donkere modus, taal, titel, geluid). */
export function usePreferences() {
  const [prefs, setPrefs] = useState<UserPreferences>(getPreferences);
  const [isDark, setIsDark] = useState(() => isDarkModeActive(prefs.themeMode, systemPrefersDark()));

  const update = useCallback((patch: Partial<UserPreferences>) => setPrefs(savePreferences(patch)), []);

  // Licht/donker, ook terwijl de app open staat (systeemwissel, klok, terugkeren naar de app).
  useEffect(() => {
    const refresh = () => setIsDark(isDarkModeActive(prefs.themeMode, systemPrefersDark()));
    refresh();
    if (prefs.themeMode !== 'auto') return;
    const mediaQuery = window.matchMedia?.('(prefers-color-scheme: dark)');
    mediaQuery?.addEventListener('change', refresh);
    const interval = window.setInterval(refresh, 60_000);
    const onVisible = () => document.visibilityState === 'visible' && refresh();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      mediaQuery?.removeEventListener('change', refresh);
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [prefs.themeMode]);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark);
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', isDark ? '#090d16' : '#eef2f6');
  }, [isDark]);

  // Taal van de pagina: html lang, titel en omschrijving
  useEffect(() => {
    document.documentElement.lang = prefs.language;
    document.title = t(prefs.language, 'metaTitle');
    document.querySelector('meta[name="description"]')?.setAttribute('content', t(prefs.language, 'metaDescription'));
  }, [prefs.language]);

  useEffect(() => {
    audioEngine.setEnabled(prefs.soundEnabled);
  }, [prefs.soundEnabled]);

  return {
    prefs,
    isDark,
    setThemeMode: (themeMode: ThemeMode) => update({ themeMode, darkMode: undefined }),
    setThemeColor: (themeColor: ThemeColor) => update({ themeColor }),
    setLanguage: (language: Language) => update({ language }),
    toggleSound: () => update({ soundEnabled: !prefs.soundEnabled })
  };
}

export type Preferences = ReturnType<typeof usePreferences>;
